import React, { useEffect, useState } from 'react';
import { Switch, Modal } from 'antd';
import {
  useUserScenesQuery,
  useUpdateUserSceneMutation,
  useCreateOneHideAndSeekMutation,
} from '../../../generated/graphql';
import HideAndSeekSettings from './HideAndSeekSettings';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import { useRouter } from 'next/router';
import styles from './hide-and-seek.module.css';

const { confirm } = Modal;

const HideAndSeek = () => {
  const [isChecked, setIsChecked] = useState(false);
  const [updateUserScene] = useUpdateUserSceneMutation();
  const [createHideAndSeek] = useCreateOneHideAndSeekMutation();
  const [hideAndSeekId, setHideAndSeekId] = useState('');

  const router = useRouter();
  const sceneId = Array.isArray(router.query?.id)
    ? router.query?.id[0]
    : router.query?.id;

  const { data: scene } = useUserScenesQuery({
    variables: {
      where: {
        id: {
          equals: sceneId as string,
        },
      },
    },
    fetchPolicy: 'no-cache',
  });

  useEffect(() => {
    if (scene?.userScenes[0].hasHideAndSeek) {
      setIsChecked(true);
      setHideAndSeekId(scene?.userScenes[0].hideAndSeekId || '');
    }
  }, [scene?.userScenes[0].hasHideAndSeek]);

  const showConfirmModal = () => {
    return new Promise((resolve) => {
      confirm({
        title: 'Confirm',
        icon: <ExclamationCircleOutlined />,
        content:
          'You are about to make hide and seek game public accessible for everybody.',
        okText: 'Ok',
        cancelText: 'Cancel',
        onOk() {
          resolve(true);
        },
        onCancel() {
          resolve(false);
        },
      });
    });
  };

  async function switchEnableHideAndSeek(checked: boolean) {
    if (checked) {
      const isConfirmed = await showConfirmModal();

      if (!isConfirmed) {
        setIsChecked(false);
        return;
      }

      setIsChecked(true);
    } else {
      setIsChecked(false);
    }

    await updateUserScene({
      variables: {
        data: {
          hasHideAndSeek: {
            set: checked,
          },
        },
        where: {
          id: sceneId,
        },
      },
      refetchQueries: ['userScenes'],
    });

    //create default values for hide and seek
    if (!scene?.userScenes[0].hideAndSeekId) {
      const hideAndSeekData = await createHideAndSeek({
        variables: {
          data: {
            UserScene: {
              connect: [
                {
                  id: sceneId,
                },
              ],
            },
            finishPrompt: 'You have captured all!',
            startPrompt:
              'Welcome to ozone hide and seek. Press start hide and seek to start game',
          },
        },
      });

      setHideAndSeekId(hideAndSeekData.data?.createOneHideAndSeek.id || '');
    } else {
      if (checked) {
        setHideAndSeekId(scene?.userScenes[0].hideAndSeekId || '');
      } else {
        setHideAndSeekId('');
      }
    }
  }

  return (
    <div className={styles.root}>
      <div className={styles.header}>
        <span className={styles.title}>Hide and Seek in the scene</span>
        <span className={styles.subtitle}>
          You can activate / deactivate Hide and Seek game with the switch
        </span>
        <Switch
          checked={isChecked}
          checkedChildren='Enabled'
          unCheckedChildren='Disabled'
          onClick={switchEnableHideAndSeek}
        />
      </div>
      {hideAndSeekId && (
        <HideAndSeekSettings hideAndSeekId={hideAndSeekId} sceneId={sceneId} />
      )}
    </div>
  );
};

export default HideAndSeek;
